import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { chromaticAberration } from 'three/addons/tsl/display/ChromaticAberrationNode.js';
import { film } from 'three/addons/tsl/display/FilmNode.js';
import { float, length, mix, pass, screenUV, smoothstep, uniform, vec2 } from 'three/tsl';
import { type Camera, RenderPipeline, type Scene, type WebGPURenderer } from 'three/webgpu';
import { type AudioFrame } from '../core/audio-frame.js';
import { Gentle } from '../core/gentle.js';
import { BeatPulse } from '../core/pulse.js';
import { Smoothed } from '../core/smoothed.js';

const BLOOM_BASE = 0.32;
const BLOOM_BASS = 0.18;
const BLOOM_BEAT = 0.1;
const BLOOM_RADIUS = 0.55;
/* Only genuinely bright things bloom — keeps the black actually black. */
const BLOOM_THRESHOLD = 0.55;
const ABERRATION_BASE = 0.14;
const ABERRATION_BEAT = 0.16;
const ABERRATION_SCALE = 1.05;
const FILM_GRAIN = 0.02;
const VIGNETTE_FLOOR = 0.42;
/** Beats decay slowly here: a long tail reads as a swell, a short one as a flash. */
const PULSE_HALF_LIFE_S = 0.42;

/**
 * The look: bloom for the neon glow, a whisper of chromatic fringing, film
 * grain, and a vignette.
 *
 * Every dial is rate-limited. The original version pushed bloom up by a full
 * unit on every kick, which at 120 bpm is a two-per-second strobe; here the
 *music drives a gentle swell, and an auto-exposure term pulls overall brightness
 * back as the mix gets louder so the average frame stays the same brightness
 * whatever the track does.
 */
export class PostStack {
  private readonly pipeline: RenderPipeline;
  private readonly renderer: WebGPURenderer;
  private readonly bloomStrength = uniform(BLOOM_BASE);
  private readonly aberrationStrength = uniform(ABERRATION_BASE);
  private readonly beatPulse = new BeatPulse(PULSE_HALF_LIFE_S);
  private readonly bass = new Smoothed(0.22);
  private readonly bloomLevel = new Gentle(0.35, 0.55, BLOOM_BASE);
  private readonly fringeLevel = new Gentle(0.4, 0.45, ABERRATION_BASE);
  private readonly exposure = new Gentle(1.6, 0.12, 1);

  constructor(renderer: WebGPURenderer, scene: Scene, camera: Camera) {
    this.renderer = renderer;
    const scenePass = pass(scene, camera);
    const bloomed = scenePass.add(
      bloom(scenePass, this.bloomStrength, BLOOM_RADIUS, BLOOM_THRESHOLD),
    );
    // Pulling the corners down concentrates the eye and hides the edge of the
    // particle field, so the composition reads as a window rather than a box.
    const fromCentre = length(screenUV.sub(0.5)).mul(1.55);
    const vignette = mix(float(VIGNETTE_FLOOR), float(1), smoothstep(1.15, 0.15, fromCentre));
    // Note: the center argument must be passed explicitly — the upstream default
    // (null) reaches the shader call unconverted and crashes the node builder.
    const fringed = chromaticAberration(
      bloomed.mul(vignette),
      this.aberrationStrength,
      vec2(0.5, 0.5),
      float(ABERRATION_SCALE),
    );
    this.pipeline = new RenderPipeline(renderer);
    this.pipeline.outputNode = film(fringed, uniform(FILM_GRAIN));
  }

  public update(frame: AudioFrame, deltaSeconds: number): void {
    const bass = this.bass.update(frame.bass, deltaSeconds);
    const pulse = this.beatPulse.update(frame, deltaSeconds);

    this.bloomStrength.value = this.bloomLevel.update(
      BLOOM_BASE + bass * BLOOM_BASS + pulse * BLOOM_BEAT,
      deltaSeconds,
    );
    this.aberrationStrength.value = this.fringeLevel.update(
      ABERRATION_BASE + pulse * ABERRATION_BEAT,
      deltaSeconds,
    );
    // Auto-exposure: louder passages get held back, so the picture swells
    // rather than blowing out. The half-life is long on purpose.
    this.renderer.toneMappingExposure = this.exposure.update(
      1 / (1 + frame.level * 0.55),
      deltaSeconds,
    );
  }

  public render(): void {
    this.pipeline.render();
  }
}
